import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import api from '@/utils/api';
import { errMsg } from '@/components/jobfairs/fairApi';

export const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export const NewFairDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const [f, setF] = useState({ name: '', slug: '', reg_prefix: '', use_template: true, start_datetime: '', end_datetime: '' });
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v, ...(k === 'name' && !slugTouched ? { slug: slugify(v) } : {}) }));

  const create = async () => {
    if (!f.name.trim() || !f.slug) return toast.error('Name and URL name are required');
    setBusy(true);
    try {
      const r = await api.post('/job-fairs/admin', f);
      toast.success('Job fair created as Draft');
      navigate(`/admin/jobfairs/${r.data.id}/edit`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-jobfair-dialog">
        <DialogHeader>
          <DialogTitle>New Job Fair</DialogTitle>
          <DialogDescription>Create the fair first; you can design every section in the editor next.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div><Label>Job Fair Name *</Label><Input data-testid="new-jobfair-name-input" value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Mega Women Job Fair 2026" /></div>
          <div>
            <Label>URL Name *</Label>
            <div className="flex items-center rounded-md border border-input overflow-hidden">
              <span className="px-3 text-sm text-slate-500 bg-slate-50 h-10 flex items-center border-r">/jobfairs/</span>
              <input data-testid="new-jobfair-slug-input" className="flex-1 h-10 px-3 text-sm outline-none" value={f.slug}
                onChange={(e) => { setSlugTouched(true); set('slug', slugify(e.target.value)); }} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Starts</Label><Input type="datetime-local" data-testid="new-jobfair-start-input" value={f.start_datetime} onChange={(e) => set('start_datetime', e.target.value)} /></div>
            <div><Label>Ends</Label><Input type="datetime-local" data-testid="new-jobfair-end-input" value={f.end_datetime} onChange={(e) => set('end_datetime', e.target.value)} /></div>
          </div>
          <div><Label>Registration ID Prefix</Label><Input data-testid="new-jobfair-prefix-input" maxLength={8} value={f.reg_prefix} onChange={(e) => set('reg_prefix', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} placeholder="MWJF → MWJF-000001" /></div>
          <label className="flex items-center justify-between gap-3 rounded-lg border p-3 cursor-pointer">
            <span className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-teal-600 mt-0.5" />
              <span><span className="font-medium text-sm">Start from Women Job Fair template</span><br /><span className="text-xs text-slate-500">Pre-fills hero, sections, steps & footer from the sample content</span></span></span>
            <Switch checked={f.use_template} onCheckedChange={(v) => set('use_template', v)} data-testid="new-jobfair-template-switch" />
          </label>
          <Button onClick={create} disabled={busy} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="new-jobfair-create-button">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Create Job Fair
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
